import React, { useMemo } from 'react';
import {
  HttpError,
  IResourceComponentsProps,
  useTranslate,
} from '@pankod/refine-core';
import {
  Avatar,
  Create,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
  Input,
  Backdrop,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
} from '@pankod/refine-mui';
import {
  Controller,
  useStepsForm,
} from '@pankod/refine-react-hook-form';
import { IPreviewBackgroundFiles, IBackgrounds } from 'interfaces';
import _ from 'lodash';
import {
  ISelectedFile,
  useFileChangeHandler,
  useUploadFile,
} from 'hooks';
import { getVSTTime } from 'utils/utils';
import { CONST } from 'config/const';
import { Radio } from '@pankod/refine-mui';

const MAX_FILE_SIZE = 2097152; // 2MB

export const BackgroundCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const t = useTranslate();

  const {
    refineCore: { onFinish, formLoading },
    control,
    register,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useStepsForm<IBackgrounds, HttpError, IBackgrounds>({
    stepsProps: {
      isBackValidate: false,
    },
    warnWhenUnsavedChanges: true,
  });

  const [format, setFormat] = React.useState<number>(CONST.FORMAT.PORTRAIT);
  const backgroundSize = useMemo(() => {
    const width = format === CONST.FORMAT.PORTRAIT ? 108 : 192;
    const height = format === CONST.FORMAT.PORTRAIT ? 192 : 108;

    return { width, height };
  }, [format]);

  // Thumbnail
  const [selectedFile, setSelectedFile] =
    React.useState<ISelectedFile>({});
  const [preview, setPreview] =
    React.useState<IPreviewBackgroundFiles>({});

  // Upload file
  const upload = useUploadFile();
  const onFileChangeHandler = useFileChangeHandler(
    setSelectedFile,
    { setError, clearErrors },
    { maxSize: MAX_FILE_SIZE }
  );

  // create a preview as a side effect, whenever selected file is changed
  React.useEffect(() => {
    const objectUrls: IPreviewBackgroundFiles = {};
    if (selectedFile?.thumb) {
      const objectUrl = URL.createObjectURL(selectedFile.thumb);
      objectUrls.thumbnail = objectUrl;
    }
    if (selectedFile?.background) {
      const objectUrl = URL.createObjectURL(selectedFile.background);
      objectUrls.background = objectUrl;
    }

    setPreview(objectUrls);

    // free memory when ever this component is unmounted
    return () => {
      Object.values(objectUrls).forEach((url) =>
        URL.revokeObjectURL(url)
      );
    };
  }, [selectedFile]);

  const handleSubmit = async () => {
    const updatedAt = getVSTTime(new Date()).toISOString();

    const [thumbnailUrl, backgroundUrl] = await Promise.all([
      upload(selectedFile.thumb),
      upload(selectedFile.background),
    ]);
    // image url
    setValue('thumb', thumbnailUrl ?? '');
    setValue('background', backgroundUrl ?? '');

    const data = {
      ...getValues(),
      updated_at: updatedAt,
    };
    onFinish(data);
  };

  const renderForm = () => {
    return (
      <>
        <Grid
          container
          sx={{
            marginX: { xs: '0px' },
          }}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item paddingX={4} xs={12}>
                <Stack gap="24px">
                  <FormControl>
                    <TextField
                      {...register('name', {
                        required: t('errors.required.field', {
                          field: 'name',
                        }),
                        maxLength: {
                          value: 100,
                          message: t('errors.required.max', {
                            max: 100,
                          }),
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      label="Name (required)"
                    />
                    {errors.name && (
                      <FormHelperText error>
                        {errors.name.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      Format
                    </Typography>
                    <Controller
                      control={control}
                      name="format"
                      defaultValue={CONST.FORMAT.PORTRAIT}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          onChange={(e, v) => {
                            setValue('format', +v);
                            setFormat(+v);
                          }}
                        >
                          <FormControlLabel
                            value={CONST.FORMAT.PORTRAIT}
                            control={<Radio />}
                            label="Portrait"
                          />
                          <FormControlLabel
                            value={CONST.FORMAT.LANDSCAPE}
                            control={<Radio />}
                            label="Landscape"
                          />
                        </RadioGroup>
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      Thumbnail
                    </Typography>
                    <Box
                      sx={{
                        height: 75,
                        width: 125,
                        border: '1px dashed grey',
                      }}
                    >
                      <label htmlFor="thumb-input">
                        <Input
                          id="thumb-input"
                          type="file"
                          sx={{
                            display: 'none',
                          }}
                          name="thumb"
                          onChange={onFileChangeHandler}
                        />
                        <input
                          id="file"
                          type="hidden"
                          {...register('thumb')}
                        />
                        <Avatar
                          variant="square"
                          sx={{
                            cursor: 'pointer',
                            width: {
                              xs: '125px',
                            },
                            height: {
                              xs: '75px',
                            },
                          }}
                          src={preview.thumbnail}
                          alt="Thumbnail"
                        />
                      </label>
                    </Box>
                    {errors.thumb && (
                      <FormHelperText error>
                        {errors.thumb.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Background */}
                  <FormControl>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      Background
                    </Typography>
                    <Box
                      sx={{
                        width: backgroundSize.width,
                        height: backgroundSize.height,
                        border: '1px dashed grey',
                      }}
                    >
                      <label htmlFor="background-input">
                        <Input
                          id="background-input"
                          type="file"
                          sx={{
                            display: 'none',
                          }}
                          name="background"
                          onChange={onFileChangeHandler}
                        />
                        <input
                          id="file"
                          type="hidden"
                          {...register('background')}
                        />
                        <Avatar
                          variant="square"
                          sx={{
                            cursor: 'pointer',
                            width: {
                              xs: `${backgroundSize.width}px`,
                            },
                            height: {
                              xs: `${backgroundSize.height}px`,
                            },
                          }}
                          src={preview.background}
                          alt="background"
                        />
                      </label>
                    </Box>
                    {errors.background && (
                      <FormHelperText error>
                        {errors.background.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Create
      title={<Typography variant="h5">Create Background</Typography>}
      isLoading={formLoading}
      saveButtonProps={{ onClick: handleSubmit }}
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        autoComplete="off"
      >
        <br />
        {renderForm()}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={formLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Create>
  );
};
